import type { AccessChange } from "@ext/enterprise/components/admin/settings/members/model/AccessChange";
import type { GesRepo, MemberAggregate } from "@ext/enterprise/components/admin/settings/members/model/Member";
import { useRepoCard } from "@ext/enterprise/components/admin/settings/resources/hooks/useRepoCard";
import { useCatalogTitle } from "@ext/enterprise/components/admin/settings/resources/hooks/useResourceCatalogs";
import { RepoForm } from "@ext/enterprise/components/admin/settings/resources/RepoForm";
import { CancelButton } from "@ext/enterprise/components/admin/ui-kit/CancelButton";
import { CloseConfirmationDialog } from "@ext/enterprise/components/admin/ui-kit/CloseConfirmationDialog";
import { SaveButton } from "@ext/enterprise/components/admin/ui-kit/SaveButton";
import { SheetComponent } from "@ext/enterprise/components/admin/ui-kit/SheetComponent";
import { SheetTitleValue } from "@ext/enterprise/components/admin/ui-kit/SheetTitleValue";
import t from "@ext/localization/locale/translate";
import { TextOverflowTooltip } from "@ui-kit/Tooltip";

interface RepoCardProps {
	open: boolean;
	repo?: GesRepo;
	repoCandidates: string[];
	aggregate: MemberAggregate;
	onClose: () => void;
	onApply: (changes: AccessChange[]) => Promise<void>;
	showEnterpriseElements: boolean;
}

const EditingCatalogTitle = ({ repoId }: { repoId: string }) => {
	const catalogTitle = useCatalogTitle(repoId);
	return (
		<span className="flex min-w-0 flex-col">
			<SheetTitleValue label={t("enterprise.admin.resources.editing")} value={catalogTitle} />
			<TextOverflowTooltip className="text-sm font-normal text-muted">{repoId}</TextOverflowTooltip>
		</span>
	);
};

export function RepoCard(props: RepoCardProps) {
	const { open, repo, showEnterpriseElements } = props;
	const { form, repoFormState } = useRepoCard(props);

	return (
		<>
			<SheetComponent
				cancelButton={
					<CancelButton
						onClick={(e) => {
							e.preventDefault();
							form.requestClose();
						}}
					/>
				}
				confirmButton={
					<SaveButton
						disabled={!repoFormState.data.repoId}
						isSaving={form.saving}
						onClick={async (e) => {
							e.preventDefault();
							await form.submit();
						}}
					/>
				}
				connectionError={form.saveError}
				isOpen={open}
				onOpenChange={(next) => !next && form.requestClose()}
				sheetContent={<RepoForm showEnterpriseElements={showEnterpriseElements} state={repoFormState} />}
				title={repo ? <EditingCatalogTitle repoId={repo.id} /> : t("enterprise.admin.resources.adding")}
			/>
			<CloseConfirmationDialog
				isOpen={form.showUnsaved}
				onClose={form.close}
				onOpenChange={form.setShowUnsaved}
			/>
		</>
	);
}
